/**
 * Security Utilities
 * Credential masking and security helpers
 */

/**
 * Mask a credential value showing only first and last 4 characters
 * Example: "abc123def456" -> "abc1...f456"
 */
export function maskCredential(value: string | null | undefined): string {
  if (!value || value.length < 8) {
    return '••••••••';
  }

  const first = value.slice(0, 4);
  const last = value.slice(-4);
  return `${first}...${last}`;
}

/**
 * Mask multiple credentials in an object
 */
export function maskCredentials(
  credentials: Record<string, any>
): Record<string, string> {
  const masked: Record<string, string> = {};

  const sensitiveFields = [
    'password',
    'apiKey',
    'api_key',
    'clientSecret',
    'client_secret',
    'securityToken',
    'security_token',
    'accessToken',
    'access_token',
    'refreshToken',
    'refresh_token',
  ];

  Object.keys(credentials).forEach((key) => {
    if (sensitiveFields.includes(key)) {
      masked[key] = maskCredential(credentials[key]);
    } else {
      masked[key] = credentials[key];
    }
  });

  return masked;
}

/**
 * Check if a string appears to be a password/key
 */
export function isSensitiveField(fieldName: string): boolean {
  const sensitivePatterns = [
    /password/i,
    /secret/i,
    /token/i,
    /key/i,
    /credential/i,
  ];

  return sensitivePatterns.some((pattern) => pattern.test(fieldName));
}

/**
 * Generate a secure random string (for IDs, tokens, etc.)
 */
export function generateRandomString(length: number = 32): string {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }

  return result;
}

/**
 * Sanitize user input to prevent XSS
 */
export function sanitizeInput(input: string): string {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
}
